import * as React from 'react';

import {RowItem} from '../RowItem';
import type {RowItemBaseProps} from '../RowItem';
import type {CheckboxProps} from '../Checkbox';

export interface RowItemCheckboxProps
  extends
    RowItemBaseProps,
    Omit<CheckboxProps, 'size' | 'prefix' | 'onChange' | 'colorVariant'> {
  /**
   * Whether the RowItem should highlight when checked.
   *
   * Defaults to `true`.
   */
  shouldHighlightOnChecked?: boolean;
}

/**
 * # RowItemCheckbox
 *
 * A `RowItem` with a built-in `Checkbox`. Can be controlled or uncontrolled.
 *
 * This is currently a Private Component and should not be used outside of the Spring System.
 * Once we settle on a good pattern for handling `onClick` vs. `onChange` we can open this up to broader usage.
 * See: @https://github.com/webflow/webflow/pull/72401#discussion_r1725729326
 *
 * ## Usage
 *
 * ```jsx
 * import {RowItemCheckbox, RowItemBody} from '@packages/systems/spring';
 *
 * const Example = () => {
 *  const [isChecked, setIsChecked] = React.useState(false);
 *  const onChange = () => setIsChecked(current => !current);
 *
 *  return (
 *   <VStack>
 *    <RowItemCheckbox>
 *      <RowItemBody>
 *        I am a checkbox!
 *      </RowItemBody>
 *    </RowItemCheckbox>
 *
 *    <RowItemCheckbox isChecked={isChecked} onChange={onChange}>
 *      <RowItemBody>
 *        I am a checkbox!
 *      </RowItemBody>
 *    </RowItemCheckbox>
 *    </VStack
 *   )
 * };
 * ```
 *
 * @component
 */
export const RowItemCheckbox = React.forwardRef<
  HTMLDivElement,
  RowItemCheckboxProps
>(
  (
    {
      defaultChecked,
      isChecked: isCheckedProp,
      onClick: onClickProp,
      role = 'checkbox',
      ...props
    }: RowItemCheckboxProps,
    ref
  ) => {
    const [uncontrolledIsChecked, setUncontrolledIsChecked] =
      React.useState(defaultChecked);

    const handleOnClick = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        if (props.isDisabled) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        onClickProp?.(event);
        setUncontrolledIsChecked((current) => !current);
      },
      [onClickProp, setUncontrolledIsChecked, props.isDisabled]
    );

    const isChecked = isCheckedProp ?? uncontrolledIsChecked;
    const {colorVariant, ...restProps} = props;

    return (
      <RowItem
        {...restProps}
        _isCheckbox
        colorVariant={colorVariant}
        data-sc="RowItemCheckbox"
        isChecked={role === 'checkbox' ? Boolean(isChecked) : undefined}
        onClick={handleOnClick}
        ref={ref}
        role={role}
      />
    );
  }
);

RowItemCheckbox.displayName = 'RowItemCheckbox';
RowItemCheckbox.toString = RowItem.toString;

export default RowItemCheckbox;
